import type { QaCheck, QaSettings } from './qa';

export type ScopeKind = 'shared' | 'tenant-group' | 'tenant';

export interface Scope {
  kind: ScopeKind;
  id?: string;
}

export type SinkKind = 'kafka' | 'http';

/**
 * How a request is attributed to a tenant. A vendor sends one stream; which tenant
 * each message belongs to has to be read out of the request itself.
 */
export type TenantResolution =
  | { kind: 'header'; name: string }
  | { kind: 'body'; path: string }
  | { kind: 'query'; name: string }
  | { kind: 'fixed'; tenantId: string }
  | { kind: 'none' };

export type IngressAuthKind = 'none' | 'api-key' | 'basic' | 'hmac-sha256';

export interface IngressAuth {
  kind: IngressAuthKind;
  /** Where the credential arrives. */
  headerName?: string;
  /** A name the runtime resolves from its own secret store. Never a secret. */
  secretRef?: string;
}

/** The endpoint a vendor actually posts to, and how we read the request. */
export interface RouteIngress {
  path: string;
  method: 'POST' | 'PUT' | 'GET';
  contentType: string;
  auth: IngressAuth;
  tenant: TenantResolution;
  /** Reject a body larger than this rather than buffering it. */
  maxBodyBytes?: number;
  /** How callers sign in on this endpoint; set once on the vendor, overridable per route. */
  authSchemeId?: string;
}

export type ExhaustedAction = 'dlq' | 'spool' | 'drop';

/** What happens when a message cannot be accepted, transformed or delivered. */
export interface ErrorPolicy {
  /** Status returned when the payload does not match the vendor schema. */
  rejectStatus: number;
  /** Copy rejected payloads somewhere they can be inspected and replayed. */
  captureRejects: boolean;
  rejectTarget?: string;
  maxRetries: number;
  retryDelayMs: number;
  backoffMultiplier: number;
  maxRetryDelayMs: number;
  onExhausted: ExhaustedAction;
  dlqTarget?: string;
  /** Collapse duplicate deliveries keyed on this request header. */
  idempotencyHeader?: string;
}

export const DEFAULT_ERROR_POLICY: ErrorPolicy = {
  rejectStatus: 422,
  captureRejects: true,
  rejectTarget: 'platform.routing.rejected',
  maxRetries: 4,
  retryDelayMs: 250,
  backoffMultiplier: 2,
  maxRetryDelayMs: 5000,
  onExhausted: 'dlq',
  dlqTarget: 'platform.routing.dlq',
  idempotencyHeader: 'X-Platform-Event-Id',
};

export interface Sink {
  kind: SinkKind;
  target: string;
  headers?: Record<string, string>;
}

export interface RouteDef {
  canarySampleId?: string;
  id: string;
  name: string;
  groupId: string;
  vendorId: string;
  canonicalSchemaId?: string;
  sink?: Sink;
  /** Overrides the vendor's ingress defaults for this route. */
  ingress?: Partial<RouteIngress>;
  /** Overrides the vendor's error policy for this route. */
  errorPolicy?: Partial<ErrorPolicy>;
  /**
   * The vendor already sends the shape the destination expects, so there is nothing to map.
   * The payload is still checked against the schema; it is simply passed through unchanged.
   */
  passThrough?: boolean;
  /** The vendor sends its bills as XML, read as JSON at the door; absent is JSON. */
  format?: 'xml';
  /** One request carries a list of documents, each of which travels on its own from here. */
  isBatch?: boolean;
  /**
   * What a payload must carry before it is accepted, as field paths.
   *
   * A schema that marks nothing as required — which is what an imported one does, because the
   * documents describe none — would accept a bill with no id and fail somewhere far away. This
   * is where that decision is made, per endpoint, by whoever knows the integration.
   */
  requiredFields?: string[];
  /**
   * The vendor puts its key in the address rather than a header, which the door cannot check, so
   * the endpoint may not go live until a developer decides how it signs in.
   */
  signInInAddress?: boolean;
  /**
   * The vendor waits for an answer (a GET, or a question such as "how many points"), so the
   * request goes straight to this Casa service and its reply comes back; nothing is mapped or
   * queued. A name, not an address: each environment says where the service lives.
   */
  upstream?: string;
  /**
   * The field a vendor would recognise, as a JSON pointer such as `/billId`.
   *
   * There is nothing on a request that a vendor knows by sight — our ids are ours, and the
   * content hash is of a body they have never seen. Without this, the journal can answer "how
   * many failed last night" but never "what happened to my bill 12345", which is the only
   * question they ever actually ask. Only the author knows which field that is.
   */
  referenceField?: string;
  /** How a QA run sends this endpoint's bills. */
  qa?: QaSettings;
  /** What a QA run checks once this endpoint's bills have gone through. */
  qaChecks?: QaCheck[];
  /**
   * Fields never written down, as JSON pointers such as `/customerMobile`.
   *
   * Keeping a payload is what makes a failure diagnosable and is also the one place the platform
   * holds somebody's personal data. The value goes and the key stays, so the shape is still
   * readable — "the field was there and we are not showing it" and "the field was missing" are
   * different failures, and telling them apart is the whole point of keeping the payload.
   */
  redactFields?: string[];
  configured: boolean;
}

export interface GroupTemplateMember {
  name: string;
  slug: string;
  /** What a route created from this member starts with, so a group configures as a unit. */
  canonicalSchemaId?: string;
  sink?: Sink;
  /** The public address a vendor calls, as the documentation writes it. */
  method?: 'GET' | 'POST' | 'PUT';
  path?: string;
  /** The docs' examples, each kept as a sample of the endpoint under the docs' name. */
  samples?: { name: string; payload: string }[];
  /** One example, as templates saved before examples were named carry it. */
  sample?: string;
  isBatch?: boolean;
  requiredFields?: string[];
  /** How callers of this one endpoint sign in, when it differs from the rest of its group. */
  authSchemeId?: string;
  /** The documentation carries this endpoint's key in its address, not in a header. */
  signInInAddress?: boolean;
}

export interface GroupTemplate {
  id: string;
  name: string;
  description?: string;
  /** The business domain this group covers, shown when grouping vendors' routes. */
  domain?: string;
  members: GroupTemplateMember[];
  version: number;
  /** Directory holding this group's Camel flows, one per member slug. */
  flowDir?: string;
  /** How callers of this group sign in; a vendor that adds the group takes it with it. */
  authSchemeId?: string;
}

export interface RouteGroup {
  id: string;
  templateId: string;
  vendorId: string;
  name: string;
  routeIds: string[];
  /** How this group's callers sign in, when it differs from the vendor's. */
  authSchemeId?: string;
  /** None of its endpoints is live for a shop until every one of them is ready for that shop. */
  strict?: boolean;
}

export interface Vendor {
  slug?: string;
  id: string;
  name: string;
  archived?: boolean;
  /** Every tenant this vendor sends for. Nothing outside this list may be resolved. */
  tenantIds: string[];
  pos?: string;
  defaultSink?: Sink;
  /** Applies to every route in the vendor unless the route overrides it. */
  ingressDefaults?: VendorIngressDefaults;
  errorPolicy?: Partial<ErrorPolicy>;
}

/** What a vendor's endpoints share by default. The address and method are never among them: those come from Casa's API docs. */
export type VendorIngressDefaults = Partial<Omit<RouteIngress, 'path' | 'method'>>;

export interface Tenant {
  id: string;
  name: string;
  vendorIds: string[];
  /** What the directory said about this tenant beyond its id and name, such as `esKey`. Never a secret. */
  details?: Record<string, string>;
}

export interface CanonicalSchemaField {
  path: string;
  type: string;
  required: boolean;
}

export interface CanonicalSchema {
  id: string;
  code: string;
  version: number;
  title: string;
  fields: CanonicalSchemaField[];
  jsonSchema?: Record<string, unknown>;
}

/** `docs` samples belong to Casa's API docs: Sync from docs refreshes them, and only them. */
export type SampleSource = 'manual' | 'captured' | 'docs';

export interface Sample {
  expectation?: 'pass' | 'reject';
  expectedViolationPath?: string;
  id: string;
  routeId: string;
  scope: string;
  name: string;
  payload: string;
  source?: SampleSource;
  capturedAt?: string;
  /** Set when the same shape arrived more than once. */
  seenCount?: number;
}

export type MappingSourceKind = 'field' | 'constant' | 'expression';

export interface FieldMapping {
  id: string;
  targetPath: string;
  sourceKind: MappingSourceKind;
  source: string;
}

export interface Draft {
  routeId: string;
  scope: string;
  mappings: FieldMapping[];
  revisionId: string;
  updatedAt: string;
  xslt?: string;
  mapperMetadata?: unknown;
  resources?: Record<string, string>;
}

export interface RuntimeArtifact {
  routeKey: string;
  scope: string;
  version: number;
  hash: string;
  xslt: string;
  xsltSha256: string;
  sourceSchema: Record<string, unknown> | null;
  targetSchema: Record<string, unknown> | null;
  sink: SinkKind;
  deliveryTarget?: Sink;
  /** One request carries a list, and each entry is accepted, queued and fails on its own. */
  batch?: boolean;
  /** The vendor contract this revision was published with, as JSON. */
  contract?: string;
  /** How this revision reshapes a document, as JSON. Absent when nobody asked for any. */
  reshape?: string;
  /** The message a document travels in, as JSON. Absent when nobody asked for one. */
  wrap?: string;
  canary: { payload: unknown } | null;
  /**
   * The Camel YAML drawn in Kaoto for this endpoint and scope, which is what the runtime runs.
   * Absent on a revision published before flows were authored; the runtime then applies the
   * stylesheet on its own, as it always did.
   */
  flow?: string;
}

export interface Revision {
  version: number;
  routeId: string;
  scope: string;
  publishedAt: string;
  publishedBy: string;
  contentHash: string;
  live: boolean;
  mappings: FieldMapping[];
  artifact?: RuntimeArtifact;
  /** The flow drawn for this endpoint, pinned so a revision always runs what it published. */
  flow?: string;
  /** The vendor contract in force for this revision, pinned for the same reason as the flow. */
  contract?: string;
  /** The reshaping in force for this revision, pinned for the same reason as the contract. */
  reshape?: string;
  /** The message wrapper in force for this revision, pinned for the same reason. */
  wrap?: string;
}

export interface TestViolation {
  path: string;
  message: string;
}

export interface TestStepTrace {
  label: string;
  detail: string;
  ok: boolean;
  /** Absent on a run saved before steps could be skipped or left unreached. */
  status?: 'pass' | 'fail' | 'skipped' | 'not-reached';
  ms?: number;
  /** What the bill looked like after this step. */
  data?: unknown;
}

/** Where one bill would have gone, and what it would have looked like; never actually sent. */
export interface TestDelivery {
  kind: 'kafka' | 'http' | 'alert';
  target?: string;
  key?: string;
  /** Header names only: their values can be secrets. */
  headers?: string[];
  body?: unknown;
  problem?: string;
}

/** One bill of a request; a batch has one per bill. */
export interface TestDocument {
  index: number;
  ok: boolean;
  trace: TestStepTrace[];
  output?: unknown;
  violations: TestViolation[];
  deliveries: TestDelivery[];
}

/** Exactly what the vendor would have been answered. */
export interface TestAnswer {
  status: number;
  body: unknown;
  /** Our side of a failure the vendor is only told vaguely about. */
  cause?: string;
}

export interface TestCaseResult {
  problem?: {
    type: string;
    title: string;
    status: number;
    detail: string;
    instance: string;
    retryable: boolean;
    violations?: TestViolation[];
  };
  expected?: 'pass' | 'reject';
  expectationMet?: boolean;
  sampleId: string;
  sampleName: string;
  ok: boolean;
  output?: unknown;
  trace: TestStepTrace[];
  violations: TestViolation[];
  documents?: TestDocument[];
  answer?: TestAnswer;
  mode?: 'draft' | 'live';
  durationMs?: number;
}

export interface TestRunResult {
  routeId: string;
  scope: string;
  revisionId: string;
  ranAt: string;
  cases: TestCaseResult[];
  passed: number;
  total: number;
  sinkSuppressed: true;
  engine?: 'preview' | 'camel-saxon';
  documentHash?: string;
}

export interface PublishBlocker {
  code:
    | 'no-samples'
    | 'no-address'
    | 'no-schema'
    | 'empty-mapping'
    | 'tests-not-run'
    | 'tests-failing'
    | 'no-sink'
    /* the flow that was drawn could not be run as it stands */
    | 'flow-unrunnable'
    /* a tenant or business canvas tried to change the address, which only the vendor sets */
    | 'address-locked'
    /* the vendor's key travels in the address, which the door cannot check */
    | 'sign-in-in-address'
    /* the docs show several kinds of bill and the flow does not tell them apart */
    | 'kinds-unsplit'
    /* one kind of bill is not configured yet: no branch, no mapping or no rules */
    | 'kind-incomplete';
  message: string;
}

export const scopeRef = (scope: Scope): string => (scope.kind === 'shared' ? 'shared' : `${scope.kind}:${scope.id}`);

export const parseScopeRef = (ref: string): Scope => {
  if (ref === 'shared') return { kind: 'shared' };
  const [kind, id] = ref.split(':');
  return { kind: kind as ScopeKind, id };
};

export const describeScopeRef = (ref: string): string => {
  const scope = parseScopeRef(ref);
  if (scope.kind === 'shared') return 'Shared';
  if (scope.kind === 'tenant-group') return `Tenant group ${scope.id}`;
  return `Tenant ${scope.id}`;
};

/**
 * Where an endpoint's authored Camel YAML is kept.
 *
 * It rides in the draft's `resources`, which already carries a scope's authored files through
 * save and publication — so the flow that was drawn reaches the runtime by the same path as the
 * stylesheet it references, and a scope that never drew one simply has no entry.
 */
export const FLOW_RESOURCE = 'route.camel.yaml';

/**
 * The one call that says which tenants exist and, inside each, which businesses.
 *
 * It is one call rather than two because the system that owns them answers with both, and asking
 * twice would mean two shapes to describe and two chances for them to disagree about which
 * business belongs where.
 */
/**
 * What a hidden header value looks like once it has left the server.
 *
 * A directory is reached with a token, and that token is a live credential. It is kept where the
 * call is made and never travels to a browser; what travels is this, and saving it back means
 * "keep the one you already have". Typing over it is how a new one is set.
 */
export const KEPT_SECRET = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022';

/** Header names that carry a credential rather than a plain instruction like `accept`. */
export const isSecretHeader = (name: string): boolean => /key|token|secret|auth|password|credential/i.test(name);

/**
 * Where Casa's field names are read from.
 *
 * Casa already knows, per tenant and business unit, that its `CF1` is this integration's
 * `division` and what a person should see it called. Asking it is the only way those names stay
 * right: typing them here would mean re-typing them every time somebody adds a custom field.
 *
 * `{{tenantId}}`, `{{businessUnitId}}` and `{{entityType}}` are written wherever those values
 * belong — in the path, a header or the body — because no two of these APIs agree on where.
 */
export interface FieldSource {
  method: 'GET' | 'POST';
  url: string;
  headers?: Record<string, string>;
  body?: string;
  /** Where the fields sit in the answer, as dotted names. Empty when the answer is the list. */
  listAt?: string;
  /** Casa's own name for the field, such as `CF1`. */
  casaFieldField?: string;
  /** What this vendor's payload calls it. */
  apiNameField?: string;
  /** What a person should see it called. */
  labelField?: string;
  /** Which kind of thing the field belongs to, such as a product. */
  entityTypeField?: string;
  /** The kind asked for when a call has to name one. */
  entityType?: string;
}

/**
 * Where a record of how a bill was delivered is read from.
 *
 * A reversal of a whole bill carries no products, so the only way to split it the way the
 * original was split is to ask whatever recorded the original. `{{billId}}`, `{{tenantId}}` and
 * `{{businessUnitId}}` are written wherever those values belong.
 */
export interface OriginalSource {
  method: 'GET' | 'POST';
  url: string;
  headers?: Record<string, string>;
  body?: string;
  /** Where the legs sit in the answer, as dotted names. Empty when the answer is the list. */
  listAt?: string;
  /** The field naming the unit on each leg, so one bill recorded twice is not reversed twice. */
  unitField?: string;
}

export interface DocsSource {
  urls: string[];
}

export interface DirectorySource {
  method: 'GET' | 'POST';
  url: string;
  headers?: Record<string, string>;
  body?: string;
  /** Where the tenants sit in the answer, as dotted names. Empty when the answer is the list. */
  listAt?: string;
  idField?: string;
  nameField?: string;
  /** The businesses inside each tenant, as a dotted path from that tenant. */
  businessesAt?: string;
  businessIdField?: string;
  businessNameField?: string;
  businessPosField?: string;
  /** The field holding a business's api-key. Only its hash is ever stored. */
  apiKeyField?: string;
  /** The field holding a key issued to the whole tenant rather than one business. Only its hash is ever stored. */
  tenantApiKeyField?: string;
  /** Other tenant fields to keep, such as `esKey`, so nodes can use them. Secrets are never kept. */
  tenantDetailFields?: string[];
}
